import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ScanLine, Trash2, Radar, Clock, Loader2, AlertTriangle, GitCompareArrows } from "lucide-react";
import ScanTerminal from "@/components/ScanTerminal";
import ReportView from "@/components/ReportView";
import ComparePage from "@/components/ComparePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESETS = [
  "Countdown timer", "Calendar", "Habit tracker", "Budget planner", "Meditation",
  "To-do list", "Note taking", "Workout tracker", "Sleep tracker", "Pomodoro focus",
  "Recipe manager", "Language learning", "Period tracker", "Water reminder", "Expense splitter",
];

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/r/:id" element={<SharedReport />} />
      </Routes>
    </BrowserRouter>
  );
}

function Dashboard() {
  const [category, setCategory] = useState("");
  const [appCount, setAppCount] = useState("10");
  const [reviewCount, setReviewCount] = useState("200");
  const [history, setHistory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeScan, setActiveScan] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/scans`);
      setHistory(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/scans/${activeId}`);
        if (cancelled) return;
        setActiveScan(data);
        if (data.status === "running") {
          pollRef.current = setTimeout(tick, 1500);
        } else {
          loadHistory();
        }
      } catch (e) { console.error(e); }
    };
    tick();
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [activeId, loadHistory]);

  const startScan = async () => {
    if (!category.trim() || starting) return;
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/scans`, {
        category: category.trim(),
        app_count: Number(appCount),
        review_count: Number(reviewCount),
      });
      setActiveId(data.id);
      setActiveScan(null);
      loadHistory();
    } catch (e) {
      console.error(e);
    } finally {
      setStarting(false);
    }
  };

  const openScan = (id) => { setActiveId(id); setActiveScan(null); };

  const deleteScan = async (id, e) => {
    e.stopPropagation();
    await axios.delete(`${API}/scans/${id}`);
    if (id === activeId) { setActiveId(null); setActiveScan(null); }
    loadHistory();
  };

  const logs = activeScan?.progress_logs || [];
  const percent = activeScan?.progress_percent || 0;
  const status = activeScan?.status;

  return (
    <div className="App min-h-screen bg-white text-zinc-900">
      {/* Sidebar */}
      <aside className="w-72 fixed inset-y-0 left-0 border-r border-zinc-200 bg-white flex flex-col z-20 no-print">
        <div className="p-6 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-[#002FA7] flex items-center justify-center rounded-sm">
              <Radar className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">REVIEWSCOPE</div>
              <div className="font-mono text-[10px] text-zinc-400 tracking-wider">play store insight agent</div>
            </div>
          </div>
          <Link to="/compare" className="mt-4 flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] font-medium" data-testid="nav-compare">
            <GitCompareArrows className="h-4 w-4" /> Compare categories
          </Link>
        </div>
        <div className="px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2">
          <Clock className="h-3 w-3" /> Scan History
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll px-2" data-testid="history-list">
          {history.length === 0 && (
            <div className="px-3 py-2 text-sm text-zinc-400">No scans yet.</div>
          )}
          {history.map((h, i) => (
            <button
              key={h.id}
              onClick={() => openScan(h.id)}
              className={`group w-full text-left px-3 py-2.5 mb-0.5 rounded-sm flex items-center gap-2 transition-colors ${
                h.id === activeId ? "bg-zinc-100 text-zinc-900 font-medium border-l-2 border-black" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 border-l-2 border-transparent"
              }`}
              data-testid={`history-item-${i}`}
            >
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm capitalize">{h.category}</span>
                <span className="block font-mono text-[10px] text-zinc-400">
                  {h.app_count} apps · {h.review_count} rev
                  {h.status === "running" && " · running…"}
                  {h.status === "failed" && " · failed"}
                </span>
              </span>
              {h.status === "running" && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
              <Trash2
                onClick={(e) => deleteScan(h.id, e)}
                className="h-3.5 w-3.5 text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-red-500"
                data-testid={`delete-scan-${i}`}
              />
            </button>
          ))}
        </div>
      </aside>

      {/* Main */}
      <main className="ml-72 min-h-screen bg-zinc-50/50 main-print">
        {/* Control strip */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-zinc-200 p-6 z-10 no-print">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">App Category</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && startScan()}
                  placeholder="Pick a preset below or type your own…"
                  className="pl-9 rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-black focus-visible:border-black h-11"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Apps</label>
              <Select value={appCount} onValueChange={setAppCount}>
                <SelectTrigger className="w-[110px] rounded-sm border-zinc-300 h-11" data-testid="app-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5" data-testid="app-count-5">Top 5</SelectItem>
                  <SelectItem value="10" data-testid="app-count-10">Top 10</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">Reviews</label>
              <Select value={reviewCount} onValueChange={setReviewCount}>
                <SelectTrigger className="w-[110px] rounded-sm border-zinc-300 h-11" data-testid="review-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="100" data-testid="review-count-100">100 each</SelectItem>
                  <SelectItem value="200" data-testid="review-count-200">200 each</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={startScan}
              disabled={starting || !category.trim()}
              className="h-11 rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold px-6"
              data-testid="scan-button"
            >
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}
              Run Scan
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 max-w-5xl">
          {!activeId && <EmptyState presets={PRESETS} active={category} onPick={(c) => setCategory(c)} />}

          {activeId && status === "running" && (
            <div className="fade-up">
              <div className="mb-5">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1">Agent Running</div>
                <h2 className="font-display text-2xl font-bold tracking-tight capitalize">{activeScan?.category}</h2>
              </div>
              <ScanTerminal logs={logs} percent={percent} />
            </div>
          )}

          {activeId && status === "failed" && (
            <div className="border border-red-200 bg-red-50 p-8 text-center fade-up" data-testid="scan-error">
              <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-red-900">Scan failed</h3>
              <p className="text-sm text-red-600 mt-1">{activeScan?.error || "Something went wrong. Try another category."}</p>
            </div>
          )}

          {activeId && status === "completed" && activeScan?.report && <ReportView scan={activeScan} />}

          {activeId && !status && (
            <div className="flex items-center gap-3 text-zinc-400 font-mono text-sm py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading scan…
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EmptyState({ presets, active, onPick }) {
  return (
    <div className="relative py-16" data-testid="empty-state">
      <div className="text-center mb-10">
        <div className="inline-flex h-14 w-14 items-center justify-center bg-zinc-900 rounded-sm mb-6">
          <Radar className="h-7 w-7 text-green-400" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight max-w-xl mx-auto leading-[1.05]">
          What should we build next?
        </h1>
        <p className="text-zinc-500 mt-4 max-w-md mx-auto leading-relaxed">
          Pick a popular app style — or type your own. The agent finds the most relevant Play Store apps, reads thousands of reviews, and ranks what users want, like, and hate.
        </p>
      </div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 text-center">Popular categories</div>
      <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
        {presets.map((c) => (
          <button
            key={c}
            onClick={() => onPick(c)}
            className={`font-mono text-xs px-3.5 py-2 border rounded-full transition-colors ${
              active.toLowerCase() === c.toLowerCase()
                ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-zinc-300 text-zinc-600 hover:border-black hover:text-black"
            }`}
            data-testid={`preset-${c.replace(/\s+/g, "-")}`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

function SharedReport() {
  const { id } = useParams();
  const [scan, setScan] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/scans/${id}`).then(({ data }) => setScan(data)).catch(() => setError(true));
  }, [id]);

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-zinc-200 px-6 sm:px-10 py-4 flex items-center gap-2 no-print">
        <div className="h-7 w-7 bg-[#002FA7] flex items-center justify-center rounded-sm">
          <Radar className="h-4 w-4 text-white" />
        </div>
        <span className="font-display font-black tracking-tight">REVIEWSCOPE</span>
        <Link to="/" className="ml-auto text-sm text-zinc-500 hover:text-zinc-900" data-testid="shared-open-app">Open the app →</Link>
      </div>
      <div className="max-w-5xl mx-auto p-6 sm:p-10">
        {error && <div className="text-center text-zinc-400 py-24" data-testid="shared-not-found">Report not found.</div>}
        {!error && !scan && <div className="flex items-center gap-3 text-zinc-400 font-mono text-sm py-24"><Loader2 className="h-4 w-4 animate-spin" /> Loading report…</div>}
        {scan && scan.status === "completed" && scan.report && <ReportView scan={scan} />}
        {scan && scan.status === "running" && <div className="text-center text-zinc-400 py-24" data-testid="shared-running">This scan is still running. Check back shortly.</div>}
        {scan && scan.status === "failed" && <div className="text-center text-red-500 py-24">This scan failed.</div>}
      </div>
    </div>
  );
}

export default App;
